import { dateTimeUtils } from '@cocoonspace/ui/lib/date-time-utils'
import { cn } from '@cocoonspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'

const dateBadgeVariants = cva(
	'flex flex-col items-center justify-center rounded leading-none',
	{
		defaultVariants: {
			inverted: false,
			size: 'default',
		},
		variants: {
			inverted: {
				false: 'bg-card border border-border',
				true: 'bg-slate-100',
			},
			size: {
				default: 'size-[67px]',
				lg: 'size-[100px]',
				sm: 'size-[50px]',
			},
		},
	},
)

export const DateBadge = ({
	day,
	tz = 'Europe/Paris',
	showYear = false,
	inverted = false,
	size = 'default',
}: {
	day: string
	tz?: string
	showYear?: boolean
} & VariantProps<typeof dateBadgeVariants>) => {
	const date = dateTimeUtils(day).tz(tz)

	return (
		<div className={cn('shrink-0', dateBadgeVariants({ inverted, size }))}>
			{showYear && (
				<div
					className={cn(
						'text-foreground/50 text-center font-bold capitalize',
						{
							'text-[10px]': size === 'sm',
							'text-sm': size === 'lg',
							'text-xs': size === 'default',
						},
						'leading-none',
					)}
				>
					{date.format('YYYY')}
				</div>
			)}

			<div
				className={cn(
					'font-bold',
					{
						'text-lg': size === 'sm',
						'text-3xl': size === 'default',
						'text-5xl': size === 'lg',
					},
					'leading-none',
				)}
			>
				{date.format('D')}
			</div>

			<div
				className={cn(
					'text-foreground/50 text-center font-bold capitalize',
					{
						'text-xs': size === 'sm',
						'text-lg': size === 'lg',
						'text-sm': size === 'default',
					},
					'leading-none',
				)}
			>
				{date.format('MMM')}
			</div>
		</div>
	)
}

export const DateBadgeSkeleton = ({
	size = 'default',
	inverted = false,
}: {
	size?: 'default' | 'lg' | 'sm'
	inverted?: boolean
}) => <div className={cn('shrink-0', dateBadgeVariants({ inverted, size }))} />
